import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FaExclamationTriangle } from 'react-icons/fa';
import { trpc } from '@/api/trpc.ts';
import { Button } from '@/components/UI';
import { describeLiveReadinessCallout, type LiveReadinessCallout, type LiveReadinessGap } from '@/utils/liveReadinessCallout';
import type { StrategyEditorSectionId } from '../strategyEditorSections';
import { getCoverageTone } from './helpers';

export interface StrategyOverviewCoverage {
    percent: number | null;
    entitiesWithBacktestsCount: number;
    totalEntitiesInUniverse: number;
    missingEntitiesCount: number;
}

export interface StrategyOverviewAttachment {
    name: string | null;
    version: number | null;
    ownerLabel: string | null;
}

/**
 * The live runtime's answer for the attached entry rules. `notChecked` means no check applies:
 * nothing is attached, or the viewer cannot edit the strategy and so cannot see its rules.
 */
export type StrategyOverviewLiveReadiness =
    | { state: 'notChecked' }
    | { state: 'checking' }
    | { state: 'failed'; message: string }
    | { state: 'checked'; gap: LiveReadinessGap | null };

interface StrategyOverviewPaneProps {
    strategyId: number;
    isOwnedStrategy: boolean;
    canEditStrategy: boolean;
    isActive: boolean;
    duration: string;
    entityTypeLabels: string[];
    hasEntityListScope: boolean;
    coverage: StrategyOverviewCoverage | null;
    entryAttachment: StrategyOverviewAttachment | null;
    exitAttachment: StrategyOverviewAttachment | null;
    screenerRuleId: number | null;
    attachedCandidateCount: number | null;
    defaultCandidateId: number | null;
    legCount: number;
    /** Short description of what the legs trade, e.g. "Long · Option". */
    legSummary: string;
    liveReadiness: StrategyOverviewLiveReadiness;
    onActivate: () => void;
    onNavigate: (sectionId: StrategyEditorSectionId) => void;
    onRecheckLiveReadiness: () => void;
}

export interface StrategyAttentionItem {
    id: string;
    message: ReactNode;
    actionLabel: string;
    onAction: () => void;
}

const StatTile = ({ label, value, note, meter }: { label: string; value: ReactNode; note?: ReactNode; meter?: ReactNode }) => (
    <div className="flex flex-col gap-1 rounded-lg border border-zinc-800 bg-[#1a1a1a] px-4 py-3">
        <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">{label}</span>
        <span className="text-xl font-semibold leading-tight tabular-nums text-white">{value}</span>
        {note !== undefined && <span className="text-xs text-zinc-500">{note}</span>}
        {meter}
    </div>
);

const PipelineStep = ({ label, value, note, isMuted = false }: { label: string; value: string; note: string; isMuted?: boolean }) => (
    <div className="flex min-w-[130px] flex-1 flex-col gap-0.5 border border-zinc-800 bg-[#1a1a1a] px-3 py-2.5 first:rounded-l-lg last:rounded-r-lg [&:not(:first-child)]:border-l-0">
        <span className="font-mono text-[9.5px] uppercase tracking-wider text-zinc-500">{label}</span>
        <span className={`truncate text-xs ${isMuted ? 'text-zinc-500' : 'font-medium text-zinc-100'}`} title={value}>
            {value}
        </span>
        <span className="truncate text-[11px] tabular-nums text-zinc-500">{note}</span>
    </div>
);

const describeAttachment = (attachment: StrategyOverviewAttachment | null): string =>
    attachment === null || attachment.version === null ? 'None' : `v${attachment.version}`;

/** The live status tile: the stored active flag, qualified by what the live runtime says about the entry rules. */
const describeLiveStatus = (
    isActive: boolean,
    liveReadiness: StrategyOverviewLiveReadiness,
    callout: LiveReadinessCallout | null
): { note: string; toneClass: string } => {
    if (liveReadiness.state === 'checking') {
        return { note: 'Checking the live runtime…', toneClass: isActive ? 'text-zinc-200' : 'text-zinc-500' };
    }

    if (liveReadiness.state === 'failed') {
        return { note: 'Live runtime check failed', toneClass: isActive ? 'text-yellow-400' : 'text-zinc-500' };
    }

    if (callout) {
        return callout.shouldRecheck
            ? { note: 'Live runtime check unavailable', toneClass: isActive ? 'text-yellow-400' : 'text-zinc-500' }
            : { note: 'Not accepted by the live runtime', toneClass: isActive ? 'text-red-400' : 'text-zinc-500' };
    }

    return isActive
        ? { note: 'Eligible for live entries', toneClass: 'text-green-400' }
        : { note: 'Not generating entries', toneClass: 'text-zinc-500' };
};

/**
 * Read-only landing section. It answers "is this strategy wired up and healthy?" before any form
 * field is shown, and routes every problem it finds to the section that fixes it.
 */
const StrategyOverviewPane = ({
    strategyId,
    isOwnedStrategy,
    canEditStrategy,
    isActive,
    duration,
    entityTypeLabels,
    hasEntityListScope,
    coverage,
    entryAttachment,
    exitAttachment,
    screenerRuleId,
    attachedCandidateCount,
    defaultCandidateId,
    legCount,
    legSummary,
    liveReadiness,
    onActivate,
    onNavigate,
    onRecheckLiveReadiness
}: StrategyOverviewPaneProps) => {
    const scorecardsQuery = useQuery({
        ...trpc.scorecards.list.queryOptions(),
        enabled: isOwnedStrategy
    });
    const strategyQuery = useQuery({
        ...trpc.strategy.fetchStrategies.queryOptions({ requireActive: false, ids: [strategyId] }),
        enabled: isOwnedStrategy
    });
    const scorecardFinalizationQuery = useQuery({
        ...trpc.scorecards.getGenerationFinalizationForStrategy.queryOptions({ strategyId }),
        enabled: isOwnedStrategy,
        refetchInterval: (query) => {
            const status = query.state.data?.status;
            return status && status !== 'not_started' && status !== 'attached' ? 5000 : false;
        }
    });

    const activeScorecardId = strategyQuery.data?.strategies[0]?.activeScorecardId ?? null;
    const activeScorecard = (scorecardsQuery.data ?? []).find((scorecard) => scorecard.id === activeScorecardId) ?? null;

    const coverageTone = getCoverageTone(coverage?.percent ?? 0);

    const attentionItems: StrategyAttentionItem[] = [];
    const scorecardFinalization = scorecardFinalizationQuery.data;

    if (scorecardFinalization && scorecardFinalization.status !== 'not_started' && scorecardFinalization.status !== 'attached') {
        attentionItems.push({
            id: 'scorecard-finalization',
            message: (
                <>
                    <b className="font-medium text-zinc-100">Scorecard setup needs attention.</b> {scorecardFinalization.message}
                </>
            ),
            actionLabel: 'Review scorecard',
            onAction: () => onNavigate('entry')
        });
    }

    if (entryAttachment === null || entryAttachment.version === null) {
        attentionItems.push({
            id: 'entry-rules',
            message: (
                <>
                    <b className="font-medium text-zinc-100">No entry rules attached.</b> This strategy cannot open positions.
                </>
            ),
            actionLabel: 'Attach entry rules',
            onAction: () => onNavigate('entry')
        });
    }

    const liveReadinessCallout =
        liveReadiness.state === 'checked' && liveReadiness.gap !== null ? describeLiveReadinessCallout(liveReadiness.gap) : null;

    if (liveReadinessCallout) {
        attentionItems.push({
            id: 'live-readiness',
            message: (
                <>
                    <b className="font-medium text-zinc-100">
                        {liveReadinessCallout.shouldRecheck
                            ? `${liveReadinessCallout.title}.`
                            : 'The live runtime does not accept the entry rules.'}
                    </b>{' '}
                    {liveReadinessCallout.reason} {liveReadinessCallout.guidance}
                </>
            ),
            ...(liveReadinessCallout.shouldRecheck
                ? { actionLabel: 'Check again', onAction: onRecheckLiveReadiness }
                : {
                      actionLabel: liveReadinessCallout.requiresRepublish ? 'Republish entry rules' : 'Review entry rules',
                      onAction: () => onNavigate('entry')
                  })
        });
    } else if (liveReadiness.state === 'failed') {
        attentionItems.push({
            id: 'live-readiness',
            message: (
                <>
                    <b className="font-medium text-zinc-100">Live readiness could not be checked.</b> {liveReadiness.message}
                </>
            ),
            actionLabel: 'Check again',
            onAction: onRecheckLiveReadiness
        });
    }

    if (exitAttachment === null || exitAttachment.version === null) {
        attentionItems.push({
            id: 'exit-rules',
            message: (
                <>
                    <b className="font-medium text-zinc-100">No exit rules attached.</b> Open positions would never be closed by rule.
                </>
            ),
            actionLabel: 'Attach exit rules',
            onAction: () => onNavigate('exit')
        });
    }

    if (coverage !== null && coverage.missingEntitiesCount > 0) {
        attentionItems.push({
            id: 'coverage',
            message: (
                <>
                    <b className="font-medium text-zinc-100">
                        {coverage.missingEntitiesCount} {coverage.missingEntitiesCount === 1 ? 'entity has' : 'entities have'} no backtest
                    </b>{' '}
                    for the current strategy version.
                </>
            ),
            actionLabel: 'Review backtests',
            onAction: () => onNavigate('backtests')
        });
    }

    const liveStatus = describeLiveStatus(isActive, liveReadiness, liveReadinessCallout);

    if (!isActive && canEditStrategy && !entityTypeLabels.includes('AssetIndex')) {
        attentionItems.push({
            id: 'inactive',
            message: (
                <>
                    <b className="font-medium text-zinc-100">Strategy is inactive.</b> It will not generate live entries.
                </>
            ),
            actionLabel: 'Set active',
            onAction: onActivate
        });
    }

    return (
        <div className="flex flex-col gap-5">
            <div>
                <h4 className="text-base font-semibold text-white">Overview</h4>
                <p className="mt-1 max-w-[58ch] text-xs text-zinc-500">
                    What this strategy is wired to right now, and what is stopping it from running.
                </p>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <StatTile
                    label="Backtest coverage"
                    meter={
                        coverage?.percent === null || coverage === null ? undefined : (
                            <div className="mt-1 h-1 overflow-hidden rounded-full bg-zinc-800">
                                <div className={`h-full rounded-full ${coverageTone.barClass}`} style={{ width: `${coverage.percent}%` }} />
                            </div>
                        )
                    }
                    note={
                        coverage === null
                            ? 'No coverage data'
                            : `${coverage.missingEntitiesCount} of ${coverage.totalEntitiesInUniverse} entities missing`
                    }
                    value={coverage?.percent === null || coverage === null ? '—' : `${coverage.percent}%`}
                />
                <StatTile
                    label="Live status"
                    note={liveStatus.note}
                    value={<span className={liveStatus.toneClass}>{isActive ? 'Active' : 'Inactive'}</span>}
                />
                <StatTile
                    label="Entry rules"
                    note={entryAttachment?.ownerLabel ?? 'Nothing attached'}
                    value={describeAttachment(entryAttachment)}
                />
                <StatTile
                    label="Exit rules"
                    note={exitAttachment?.ownerLabel ?? 'Nothing attached'}
                    value={describeAttachment(exitAttachment)}
                />
            </div>

            {attentionItems.length > 0 && (
                <div className="overflow-hidden rounded-lg border border-yellow-700/40 bg-yellow-950/10">
                    <div className="flex items-center gap-2 border-b border-yellow-700/25 px-4 py-2.5 text-xs font-semibold text-yellow-300">
                        <FaExclamationTriangle aria-hidden="true" />
                        Needs attention
                    </div>
                    {attentionItems.map((item) => (
                        <div
                            className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 text-xs text-zinc-400 [&:not(:first-of-type)]:border-t [&:not(:first-of-type)]:border-yellow-700/15"
                            key={item.id}
                        >
                            <span className="min-w-0">{item.message}</span>
                            <Button size="sm" variant="outline-dark" onClick={item.onAction}>
                                {item.actionLabel}
                            </Button>
                        </div>
                    ))}
                </div>
            )}

            <div>
                <h5 className="mb-2 text-xs font-semibold text-zinc-300">Signal pipeline</h5>
                <div className="flex overflow-x-auto pb-1">
                    <PipelineStep
                        label="Universe"
                        note={`${entityTypeLabels.join(', ') || '—'} · ${duration}`}
                        value={hasEntityListScope ? 'Entity list' : 'All monitored'}
                    />
                    <PipelineStep
                        isMuted={screenerRuleId === null}
                        label="Screener"
                        note={screenerRuleId === null ? '—' : `#${screenerRuleId}`}
                        value={screenerRuleId === null ? 'None' : 'Attached'}
                    />
                    <PipelineStep
                        isMuted={entryAttachment?.name == null}
                        label="Entry rules"
                        note={entryAttachment?.version === null || entryAttachment === null ? '—' : `v${entryAttachment.version}`}
                        value={entryAttachment?.name ?? 'None'}
                    />
                    <PipelineStep
                        isMuted={activeScorecard === null}
                        label="Scorecard"
                        note={activeScorecard === null ? '—' : 'Active'}
                        value={activeScorecard?.name ?? 'None active'}
                    />
                    <PipelineStep
                        isMuted={legCount === 0}
                        label="Position"
                        note={legSummary}
                        value={legCount === 0 ? 'No legs' : `${legCount} ${legCount === 1 ? 'leg' : 'legs'}`}
                    />
                    <PipelineStep
                        isMuted={exitAttachment?.name == null}
                        label="Exit rules"
                        note={exitAttachment?.version === null || exitAttachment === null ? '—' : `v${exitAttachment.version}`}
                        value={exitAttachment?.name ?? 'None'}
                    />
                    <PipelineStep
                        isMuted={(attachedCandidateCount ?? 0) === 0}
                        label="Candidates"
                        note={defaultCandidateId === null ? 'no default' : `default #${defaultCandidateId}`}
                        value={attachedCandidateCount === null ? '—' : `${attachedCandidateCount} attached`}
                    />
                </div>
            </div>

            <div className="flex flex-wrap gap-2">
                <Button size="sm" to={`/user/strategyDashboard/${strategyId}`} variant="outline-dark">
                    Open dashboard
                </Button>
                {isOwnedStrategy && (
                    <>
                        <Button size="sm" to={`/user/entryOptimizer/new?strategyId=${strategyId}`} variant="outline-dark">
                            Entry Optimizer
                        </Button>
                        <Button size="sm" to={`/user/aero/new?strategyId=${strategyId}`} variant="outline-dark">
                            AERO
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
};

export default StrategyOverviewPane;
